import { useState, useEffect } from "react";
import TenantLayout from "@/pages/tenant/components/TenantLayout";
import { getTenantPlans, buyTenantPlan, type TenantPlan, type TenantPlanInfo } from "@/utils/tenantApi";

function formatDate(dateStr: string) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function TenantPlansPage() {
  const [data, setData] = useState<TenantPlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [purchasingPlanId, setPurchasingPlanId] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await getTenantPlans();
      setData(res);
    } catch (err: any) {
      console.error('获取套餐失败:', err);
      showToast(err.message || "获取数据失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleBuyPlan = async (planId: string) => {
    if (!confirm("确定要购买此套餐吗？")) return;
    try {
      setPurchasingPlanId(planId);
      const res = await buyTenantPlan(planId);
      showToast(`订单已创建，金额：¥${res.amount}`);
      setShowModal(true);
    } catch (err: any) {
      showToast(err.message || "购买失败");
    } finally {
      setPurchasingPlanId(null);
    }
  };

  const currentPlan = data?.current_plan;
  const plans = data?.plans || [];

  return (
    <TenantLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-800">套餐管理</h2>
        </div>

        {/* Current Plan */}
        {currentPlan && (
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm mb-1">当前套餐</p>
                <h3 className="text-2xl font-bold mb-2">{currentPlan.name}</h3>
                <p className="text-orange-100 text-sm">
                  到期时间：{currentPlan.expire_at ? formatDate(currentPlan.expire_at) : '永久'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-orange-100 text-sm mb-1">站点数量限制</p>
                <p className="text-3xl font-bold">
                  {currentPlan.site_limit > 0 ? currentPlan.site_limit : '∞'}
                </p>
                <p className="text-orange-100 text-sm">个站点</p>
              </div>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-2xl text-gray-400" />
          </div>
        ) : plans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {plans.map((plan) => {
              const isCurrent = currentPlan?.id === plan.id;
              const benefits = plan.benefits || {};
              const siteLimit = benefits.site_limit || -1;
              
              return (
                <div 
                  key={plan.id} 
                  className={`bg-white rounded-2xl border p-6 ${
                    isCurrent ? 'border-orange-300 ring-2 ring-orange-100' : 'border-gray-100'
                  }`}
                >
                  {isCurrent && (
                    <div className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-full inline-block mb-3">
                      当前套餐
                    </div>
                  )}
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">{plan.name}</h4>
                  
                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-orange-500">¥{plan.amount}</span>
                      <span className="text-sm text-gray-400">元</span>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <i className="ri-global-line text-gray-400" />
                        <span className="text-sm text-gray-600">建站数量</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-800">
                        {siteLimit > 0 ? `${siteLimit} 个` : '不限'}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleBuyPlan(plan.id)}
                    disabled={isCurrent || purchasingPlanId === plan.id}
                    className={`w-full py-2.5 text-sm font-medium rounded-xl transition-colors cursor-pointer ${
                      isCurrent
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : purchasingPlanId === plan.id
                        ? "bg-orange-100 text-orange-400 cursor-wait"
                        : "bg-orange-500 text-white hover:bg-orange-600"
                    }`}
                  >
                    {isCurrent ? '当前套餐' : purchasingPlanId === plan.id ? '购买中...' : '购买此套餐'}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <i className="ri-vip-crown-line text-3xl mb-2" />
            <p className="text-sm">暂无可购买的套餐</p>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gray-800 text-white text-sm rounded-xl shadow-lg">
            <i className="ri-checkbox-circle-line text-green-400" />
            {toast}
          </div>
        )}
      </div>
    </TenantLayout>
  );
}
